import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import EnhancedLegend from './EnhancedLegend';

const CustomPieChart = ({ 
  data = [], 
  title = '', 
  dataKey = 'value', 
  nameKey = 'name',
  colors = [
    'var(--accent)',
    'color-mix(in srgb, var(--accent) 82%, var(--surface) 18%)',
    'color-mix(in srgb, var(--accent) 70%, var(--surface) 30%)',
    'color-mix(in srgb, var(--accent) 58%, var(--surface) 42%)',
    'color-mix(in srgb, var(--accent) 46%, var(--surface) 54%)',
    'color-mix(in srgb, var(--accent) 36%, var(--surface) 64%)',
    'color-mix(in srgb, var(--accent) 28%, var(--surface) 72%)',
    'color-mix(in srgb, var(--accent) 22%, var(--surface) 78%)',
    'color-mix(in srgb, var(--accent) 16%, var(--surface) 84%)',
    'color-mix(in srgb, var(--accent) 10%, var(--surface) 90%)'
  ],
  showLegend = true,
  showTooltip = true,
  className = '',
  height = 300,
  useEnhancedLegend = true,
  maxLegendItems = 5,
  sortDescending = true
}) => {
  const total = data.reduce((sum, item) => sum + (item[dataKey] || 0), 0);

  const formatTooltip = (value, name, _props) => {
    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
    return [`${value.toLocaleString('pt-BR')} (${percentage}%)`, name];
  };

  return (
    <div className={`${className} [&_.recharts-sector]:outline-none [&_.recharts-sector:focus]:outline-none [&_.recharts-sector:focus-visible]:outline-none`}>
      {title && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">{title}</h3>
        </div>
      )}
      
      <div className="flex items-center justify-center">
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={false}
              outerRadius={height * 0.35}
              fill="#8884d8"
              dataKey={dataKey}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={colors[index % colors.length]}
                  focusable="false"
                  tabIndex={-1}
                  style={{ outline: 'none' }}
                />
              ))}
            </Pie>
            {showTooltip && (
              <Tooltip
                formatter={formatTooltip}
                contentStyle={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-primary)',
                  boxShadow: 'var(--shadow)'
                }}
                labelStyle={{ color: 'var(--text-primary)' }}
                itemStyle={{ color: 'var(--text-primary)' }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      </div>
      
      {/* Legenda customizada melhorada */}
      {showLegend && data.length > 0 && (
        <div className="mt-3">
          {useEnhancedLegend ? (
            <div className="max-h-64 overflow-y-auto">
              <EnhancedLegend
                data={data}
                dataKey={dataKey}
                nameKey={nameKey}
                colors={colors}
                maxItems={maxLegendItems}
                showToggle={data.length > maxLegendItems}
                sortDescending={sortDescending}
              />
            </div>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {data.map((item, index) => {
                const percentage = total > 0 ? ((item[dataKey] / total) * 100).toFixed(1) : 0;
                return (
                  <div key={index} className="flex items-center space-x-2 p-2 bg-[color:var(--surface-muted)] rounded hover:bg-[color:var(--surface-muted)] transition-colors">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0 shadow-sm" 
                      style={{ backgroundColor: colors[index % colors.length] }}
                    />
                    <div className="flex-1 min-w-0">
                      <span 
                        className="font-medium text-[color:var(--text-primary)] text-xs block break-words" 
                        title={item[nameKey]}
                      >
                        {item[nameKey]}
                      </span>
                    </div>
                    <div className="flex flex-col items-end ml-2">
                      <span className="text-[color:var(--text-muted)] font-bold text-xs">
                        {percentage}%
                      </span>
                      <span className="text-xs text-[color:var(--text-muted)]">
                        {item[dataKey].toLocaleString('pt-BR')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      
      {data.length === 0 && (
        <div className="text-center py-8 text-[color:var(--text-muted)]">
          Nenhum dado disponível
        </div>
      )}
    </div>
  );
};

export default CustomPieChart;
